/**
 * Camera math for a pannable, zoomable 2D canvas — the Graph page's, shared
 * with the DB Manager's ER diagram so both zoom, fit and glide the same way.
 * Pure: no DOM, no three.js; the callers own the camera itself.
 */

/** Wheel sensitivity: one notch (deltaY ≈ 100) zooms by ~16 %. */
export const WHEEL_ZOOM_SPEED = 0.0015;
/** How long an animated camera move (fit, focus, zoom buttons) takes. */
export const CAMERA_ANIM_MS = 380;

export function clampZoom(zoom, min, max) {
  return Math.min(max, Math.max(min, zoom));
}

/** The zoom after one wheel event — exponential, so in and out feel symmetric. */
export function wheelZoom(zoom, deltaY, min, max) {
  return clampZoom(zoom * Math.exp(-deltaY * WHEEL_ZOOM_SPEED), min, max);
}

/**
 * The zoom that shows a `width` × `height` world box in a `viewWidth` ×
 * `viewHeight` viewport with a margin around it, never closer than `maxZoom`.
 * A tiny box (one node) counts as `minSpan` wide so fitting it does not zoom
 * in absurdly far.
 */
export function fitZoom(width, height, viewWidth, viewHeight, { maxZoom, minSpan = 80, margin = 0.85 }) {
  const w = Math.max(minSpan, width);
  const h = Math.max(minSpan, height);
  return Math.min(maxZoom, margin * Math.min(viewWidth / w, viewHeight / h));
}

/**
 * The camera at `t` ∈ [0, 1] of a move from `from` to `to` ({ x, y, zoom }):
 * ease-out cubic for the position, geometric for the zoom (so zooming 1 → 4
 * feels as even as 4 → 1).
 */
export function easeCamera(from, to, t) {
  const k = 1 - (1 - Math.min(1, Math.max(0, t))) ** 3;
  return {
    x: from.x + (to.x - from.x) * k,
    y: from.y + (to.y - from.y) * k,
    zoom: from.zoom * (to.zoom / from.zoom) ** k,
  };
}
